import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { AlertCircle, Save, RotateCcw, Bell } from 'lucide-react';
import Button from '@components/common/Button';
import { Input, Checkbox, Select } from '@components/common';
import type { NotificationSetting, NotificationConfigField, NotificationConfigValue } from '@app-types';
import styles from './NotificationEditor.module.css';

type FormValues = Record<string, NotificationConfigValue>;

export interface NotificationEditorProps {
  notification: NotificationSetting | undefined;
  isSaving: boolean;
  isResetting: boolean;
  onSave: (config: FormValues) => void;
  onResetClick: () => void;
  /** Notifies the parent when the form's dirty (unsaved) state changes. */
  onDirtyChange?: (dirty: boolean) => void;
}

/** Build the form's default values from the effective config (or schema defaults). */
function buildDefaults(notification: NotificationSetting): FormValues {
  const values: FormValues = {};
  for (const field of notification.config_schema) {
    values[field.key] = notification.config[field.key] ?? field.default;
  }
  return values;
}

function addRole(selected: string[], role: string): string[] {
  return [...selected, role];
}

function removeRole(selected: string[], role: string): string[] {
  return selected.filter((value) => value !== role);
}

/** One checkbox in a "role-list" field, kept as its own component to keep JSX nesting shallow. */
const RoleListOption: React.FC<{
  fieldKey: string;
  option: { value: string; label: string };
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ fieldKey, option, selected, onChange }) => (
  <Checkbox
    label={option.label}
    checked={selected.includes(option.value)}
    onChange={(e) =>
      onChange(e.target.checked ? addRole(selected, option.value) : removeRole(selected, option.value))
    }
    dataTestId={`config-${fieldKey}-${option.value}`}
  />
);

const NotificationEditor: React.FC<NotificationEditorProps> = ({
  notification,
  isSaving,
  isResetting,
  onSave,
  onResetClick,
  onDirtyChange
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: notification ? buildDefaults(notification) : {}
  });

  // Re-initialize the form whenever the selected notification changes, then
  // validate up-front so out-of-bounds persisted values surface immediately
  // (not only after the user first edits the field).
  useEffect(() => {
    if (notification) {
      reset(buildDefaults(notification));
      void trigger();
    }
  }, [notification, reset, trigger]);

  // Bubble the dirty state up so the page can guard navigation / show indicators.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  if (!notification) {
    return (
      <div className={styles.noSelection} data-test-id="notification-editor-empty">
        <Bell size={48} />
        <p>Select a notification to edit</p>
      </div>
    );
  }

  const isInvalid = Object.keys(errors).length > 0;

  const renderField = (field: NotificationConfigField): React.ReactNode => {
    const errorMessage = errors[field.key]?.message as string | undefined;

    switch (field.type) {
      case 'boolean':
        return (
          <Controller
            control={control}
            name={field.key}
            render={({ field: rhf }) => (
              <Checkbox
                label={field.label}
                checked={Boolean(rhf.value)}
                onChange={(e) => rhf.onChange(e.target.checked)}
                dataTestId={`config-${field.key}`}
              />
            )}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            label={field.label}
            error={errorMessage}
            dataTestId={`config-${field.key}`}
            {...register(field.key, {
              valueAsNumber: true,
              validate: {
                required: (v) => (v !== undefined && v !== null && !Number.isNaN(v as number)) || 'Required',
                // Skip the remaining rules when empty (NaN) so only "Required" shows.
                integer: (v) => Number.isNaN(v as number) || Number.isInteger(v as number) || 'Must be a whole number',
                min: (v) => field.min === undefined || Number.isNaN(v as number) || (v as number) >= field.min || `Must be ≥ ${field.min}`,
                max: (v) => field.max === undefined || Number.isNaN(v as number) || (v as number) <= field.max || `Must be ≤ ${field.max}`
              }
            })}
          />
        );

      case 'select':
        return (
          <Controller
            control={control}
            name={field.key}
            rules={{
              validate: (v) =>
                (field.options ?? []).some((opt) => opt.value === v) || 'Select a valid option'
            }}
            render={({ field: rhf }) => (
              <Select
                label={field.label}
                options={field.options ?? []}
                value={(rhf.value as string) ?? ''}
                onChange={rhf.onChange}
                error={errorMessage}
                dataTestId={`config-${field.key}`}
              />
            )}
          />
        );

      case 'role-list':
        return (
          <Controller
            control={control}
            name={field.key}
            rules={{
              validate: (v) => {
                const allowed = (field.options ?? []).map((opt) => opt.value);
                return (v as string[]).every((role) => allowed.includes(role)) || 'Invalid role selected';
              }
            }}
            render={({ field: rhf }) => {
              const selected = (rhf.value as string[]) ?? [];
              const errorId = `config-${field.key}-error`;
              return (
                <fieldset
                  className={styles.roleList}
                  aria-describedby={errorMessage ? errorId : undefined}
                >
                  <legend className={styles.roleListLegend}>{field.label}</legend>
                  {(field.options ?? []).map((opt) => (
                    <RoleListOption
                      key={opt.value}
                      fieldKey={field.key}
                      option={opt}
                      selected={selected}
                      onChange={rhf.onChange}
                    />
                  ))}
                  {errorMessage && (
                    <span id={errorId} className={styles.roleListError} role="alert" data-test-id={errorId}>
                      {errorMessage}
                    </span>
                  )}
                </fieldset>
              );
            }}
          />
        );

      case 'text':
      default:
        return (
          <Input
            type="text"
            label={field.label}
            error={errorMessage}
            dataTestId={`config-${field.key}`}
            {...register(field.key)}
          />
        );
    }
  };

  return (
    <main className={styles.editorPanel} data-test-id="notification-editor">
      <div className={styles.editorHeader}>
        <h2 data-test-id="editor-title">{notification.name}</h2>
        <p className={styles.description}>{notification.description}</p>
        {notification.is_modified && (
          <div className={styles.modifiedBadge} data-test-id="modified-badge">
            <AlertCircle size={14} />
            <span>Modified from default</span>
          </div>
        )}
      </div>

      <form className={styles.form} onSubmit={handleSubmit(onSave)} data-test-id="notification-form">
        <div className={styles.fields}>
          {notification.config_schema.map((field) => (
            <div key={field.key} className={styles.formField} data-test-id={`config-field-${field.key}`}>
              {renderField(field)}
              {field.hint && <span className={styles.hint}>{field.hint}</span>}
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            disabled={!isDirty || isInvalid}
            loading={isSaving}
            loadingText="Saving..."
            data-test-id="save-button"
          >
            <Save size={16} />
            Save Changes
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onResetClick}
            disabled={!notification.is_modified || isResetting}
            data-test-id="reset-button"
          >
            <RotateCcw size={16} />
            Reset to Default
          </Button>
        </div>
      </form>
    </main>
  );
};

export default NotificationEditor;
